import { createSelector, ParametricSelector } from 'reselect'
import { State } from 'interfaces/state'
import { OpportunityCategory, OpportunitiesPageTitlesMap, WithSelectedCategoryId, OpportunityPlacement } from 'interfaces/opportunities'
import { metaSelector, createSelectedCategoryIdSelector } from 'selectors/globalSelectors'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import { Category, OpportunityCategoryId } from 'interfaces/meta'
import { createFakeInsight } from 'helpers/insightHelpers'
import {
  NO_DISMISSED_OPPORTUNITIES_TEXT,
  NO_NEW_OPPORTUNITIES_TEXT,
  NO_OPPORTUNITIES_TEXT,
  OPPORTUNITIES_PAGE_PLACEMENT,
  OPPORTUNITIES_CATEGORY_TITLES,
  OPPORTUNITIES_PAGE_TITLES,
} from 'constants/opportunities'
import { shapeOpportunityCategories } from 'helpers/opportunityHelpers'
import { insightsDataSelector, createVisibleInsightsByPlacementSelector } from 'selectors/insightsSelectors'
import { getStaleableData } from 'helpers/storeHelpers'
import { SocialInnerInsightsCollection, SocialInnerInsight } from 'interfaces/socialInsights'
import find from 'lodash/find'
import { WithPlacement } from 'interfaces/insights'
import { OPPORTUNITIES_PAGE } from 'constants/pages'

type RawCategory = {
  id: OpportunityCategoryId
  value: string
}


export const opportunityCategoryTitlesSelector = createSelector(
  metaSelector,
  (meta): OpportunitiesPageTitlesMap => {
    const {
      income_categories,
      spending_categories,
      debt_categories,
      asset_categories,
    } = meta

    const categoryTitles: OpportunitiesPageTitlesMap = {}
    const categories = [
      ...getItemsFromCollection<Category, 'id'>(income_categories),
      ...getItemsFromCollection<Category, 'id'>(spending_categories),
      ...getItemsFromCollection<Category, 'id'>(debt_categories),
      ...getItemsFromCollection<Category, 'id'>(asset_categories),
    ] as RawCategory[]

    categories.forEach((category) => {
      categoryTitles[category.id] = category.value
    })

    return {
      ...categoryTitles,
      ...OPPORTUNITIES_CATEGORY_TITLES,
    }
  },
)

// `Opportunities` is a temp term for entity combining offers and opportunities, like `moneyflow` combining `spending` and `incomee`
export const createOpportunitiesPageTitleSelector = (page: typeof OPPORTUNITIES_PAGE) => {
  const selectedCategoryIdSelector = createSelectedCategoryIdSelector()

  return createSelector(
    opportunityCategoryTitlesSelector,
    (state: State) => selectedCategoryIdSelector(state, page),
    (titles: any, categoryId) => {
      return OPPORTUNITIES_PAGE_TITLES[categoryId] || `${ titles[categoryId] } Opportunities`
    },
  )
}

export const opportunitiesSelector = createSelector(
  insightsDataSelector,
  (insights): SocialInnerInsightsCollection | null =>
    insights
      ? getStaleableData(insights[OPPORTUNITIES_PAGE_PLACEMENT])
      : null

)

export const createOpportunitiesCategoriesSelector = (placement: OpportunityPlacement) => createSelector(
  opportunityCategoryTitlesSelector,
  createVisibleInsightsByPlacementSelector<SocialInnerInsight>(placement),
  metaSelector,
  shapeOpportunityCategories,
)

export const opportunityCategoriesSelector: ParametricSelector<State, WithPlacement<OpportunityPlacement>, OpportunityCategory[]> = createOpportunitiesCategoriesSelector(OPPORTUNITIES_PAGE_PLACEMENT)

export const opportunityCountersSelector = createSelector(
  opportunityCategoriesSelector,
  (opportunityCategories) => {
    if (!opportunityCategories) {
      return null
    }

    return opportunityCategories
      .reduce<Partial<Record<OpportunityCategory['id'], number>>>(
        (result, category) => ({
          ...result,
          [category.id]: category.opportunities
            .filter(({ is_dismissed }) => !is_dismissed)
            .length,
        }),
        {},
      )
  },
)

export const createRawFilteredOpportunitiesSelector = (): ParametricSelector<State, WithPlacement<OpportunityPlacement>, SocialInnerInsight[] | null> => {
  const selectedCategoryIdSelector = createSelectedCategoryIdSelector()
  return createSelector(
    (state: State) => selectedCategoryIdSelector(state, OPPORTUNITIES_PAGE),
    opportunityCategoriesSelector,
    (selectedCategoryId, categories) => {
      if (!categories) {
        return null
      }

      const selectedCategory = categories.find((category) => category.id === selectedCategoryId)

      return selectedCategory && selectedCategory.opportunities.length
        ? selectedCategory.opportunities
        : []
    },
  )
}


export const createFilteredOpportunitiesSelector = (placement: OpportunityPlacement): ParametricSelector<State, WithPlacement<OpportunityPlacement> & WithSelectedCategoryId, SocialInnerInsight[]> => {
  const categoriesSelector = createOpportunitiesCategoriesSelector(placement)
  const selectedCategoryIdSelector = (_state: State, { selectedCategoryId }: WithSelectedCategoryId) => selectedCategoryId

  return createSelector(
    selectedCategoryIdSelector,
    categoriesSelector,
    (selectedCategoryId, categories) => {
      if (!categories) {
        return []
      }

      const selectedCategory = categories.find((category) => category.id === selectedCategoryId)

      if (selectedCategory && selectedCategory.opportunities.length) {
        return selectedCategory.opportunities || []
      }

      const DEFAULT_EMPTY_STATE: Partial<SocialInnerInsight> = {
        placement: OPPORTUNITIES_PAGE_PLACEMENT,
        type: 'zero_state',
        tags: [],
        text: NO_OPPORTUNITIES_TEXT,
      }

      switch (selectedCategoryId) {
        case 'dismissed':
          return [
            createFakeInsight<SocialInnerInsight>({
              ...DEFAULT_EMPTY_STATE,
              is_dismissed: true,
              text: NO_DISMISSED_OPPORTUNITIES_TEXT,
            }),
          ]

        case 'new':
          const topCategory = categories.find((category) => category.id === 'top')
          if (topCategory && topCategory.opportunities.length) {
            return [
              createFakeInsight<SocialInnerInsight>({
                ...DEFAULT_EMPTY_STATE,
                text: NO_NEW_OPPORTUNITIES_TEXT,
                cta: {
                  link: {
                    target: OPPORTUNITIES_PAGE_PLACEMENT,
                    title: 'See Top',
                    filters: {
                      opportunities_filter: 'top',
                      start_date: '',
                      end_date: '',
                    },
                  },
                },
              }),
            ]
          } else {
            return [createFakeInsight<SocialInnerInsight>(DEFAULT_EMPTY_STATE)]
          }

        default:
          return [createFakeInsight<SocialInnerInsight>(DEFAULT_EMPTY_STATE)]
      }
    },
  )
}

const sortInsightsByMatch = (a: SocialInnerInsight, b: SocialInnerInsight) => {
  const matchA = a.match ? a.match.value : 0
  const matchB = b.match ? b.match.value : 0
  return matchB - matchA
}

export const createOpportunitiesCategoryGroupSelector = (placement: OpportunityPlacement) => {
  const EMPTY_PLACEMENT_OBJ = {
    placement,
  }
  const categoriesSelector = createOpportunitiesCategoriesSelector(placement)
  return createSelector(
    (state: State) => categoriesSelector(state, EMPTY_PLACEMENT_OBJ),
    (_state: State, categoryId: OpportunityCategoryId) => categoryId,
    (opportunities, categoryId) => {
      const category = find(opportunities, { id: categoryId })
      if (!category) {
        return null
      }

      return [...category.opportunities]
        .sort(sortInsightsByMatch)
        .slice(0, 2)
    }
  )
}
